/**
 * https://github.com/uidotdev/usehooks/blob/main/index.js
 * how to use: https://usehooks.com/uselocalstorage
 */
import { Dispatch, useCallback, useEffect, useSyncExternalStore } from 'react';
import { jsonParse } from '../utils';

function dispatchStorageEvent(key: string, newValue: string | null) {
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
}
const setLocalStorageItem = <T>(key: string, value: T) => {
  const stringifiedValue = JSON.stringify(value);
  window.localStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
};

const removeLocalStorageItem = (key: string) => {
  window.localStorage.removeItem(key);
  dispatchStorageEvent(key, null);
};

const getLocalStorageItem = (key: string): string => {
  return window.localStorage.getItem(key) as string;
};

const useLocalStorageSubscribe = <T>(callback: (v: T) => void) => {
  window.addEventListener('storage', callback as any);
  return () => window.removeEventListener('storage', callback as any);
};

const getLocalStorageServerSnapshot = () => {
  throw Error('useLocalStorage is a client-only hook');
};

export function useLocalStorage<T extends string | number | boolean>(
  key: string,
  initialValue?: T
): [T, Dispatch<React.SetStateAction<T>>] {
  const getSnapshot = () => getLocalStorageItem(key);

  const store = useSyncExternalStore(useLocalStorageSubscribe, getSnapshot, getLocalStorageServerSnapshot);

  const setState = useCallback(
    (v: ((_v: any) => T) | T) => {
      try {
        const nextState = typeof v === 'function' ? v(JSON.parse(store)) : v;

        if (nextState === undefined || nextState === null) {
          removeLocalStorageItem(key);
        } else {
          setLocalStorageItem(key, nextState);
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [key, store]
  );

  useEffect(() => {
    if (getLocalStorageItem(key) === null && typeof initialValue !== 'undefined') {
      setLocalStorageItem(key, initialValue);
    }
  }, [key, initialValue]);

  return [store ? jsonParse(store, initialValue) : initialValue, setState];
}
